import {
  clearTenantScope,
  getTenantScope,
  setImpersonationReason,
  sanitizeTenantScopeId,
  setActivePartitionScope,
  setOrganizationScope,
  type TenantScope,
} from '../security/tenantScope';

/** Saved email on sign-in (optional). */
export const AUTH_REMEMBERED_EMAIL_KEY = 'auth_remembered_email';

/**
 * When `'0'`, access/refresh tokens live in sessionStorage only (cleared when the browser session ends).
 * When unset or `'1'`, tokens use localStorage (classic "stay signed in").
 */
export const AUTH_REMEMBER_DEVICE_KEY = 'cosellus_auth_remember_device';

const ACCESS_TOKEN_STORAGE_KEY = 'cosellus_token';
const REFRESH_TOKEN_STORAGE_KEY = 'cosellus_refresh_token';

const AUTH_TOKEN_KEYS = [ACCESS_TOKEN_STORAGE_KEY] as const;
const REFRESH_TOKEN_KEYS = [REFRESH_TOKEN_STORAGE_KEY] as const;

const readFromStorage = (storage: Storage | null, key: string): string | null => {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const getStorages = (): Array<Storage | null> => [
  typeof window !== 'undefined' ? window.localStorage : null,
  typeof window !== 'undefined' ? window.sessionStorage : null,
];

export const getAccessToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return (
    readFromStorage(window.localStorage, ACCESS_TOKEN_STORAGE_KEY) ??
    readFromStorage(window.sessionStorage, ACCESS_TOKEN_STORAGE_KEY)
  );
};

export const getRefreshToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return (
    readFromStorage(window.localStorage, REFRESH_TOKEN_STORAGE_KEY) ??
    readFromStorage(window.sessionStorage, REFRESH_TOKEN_STORAGE_KEY)
  );
};

export const getBestAuthToken = (): string | null => {
  for (const storage of getStorages()) {
    for (const key of AUTH_TOKEN_KEYS) {
      const token = readFromStorage(storage, key);
      if (token) return token;
    }
  }
  return null;
};

export const getBestRefreshToken = (): string | null => {
  for (const storage of getStorages()) {
    for (const key of REFRESH_TOKEN_KEYS) {
      const token = readFromStorage(storage, key);
      if (token) return token;
    }
  }
  return null;
};

export function setSessionPersistencePreference(rememberDevice: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(AUTH_REMEMBER_DEVICE_KEY, rememberDevice ? '1' : '0');
  } catch {
    // Best-effort only.
  }
}

/** Legacy default: treat unset as remember so existing users remain signed in. */
export function getSessionPersistsToLocalStorage(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(AUTH_REMEMBER_DEVICE_KEY) !== '0';
  } catch {
    return true;
  }
}

export function clearSessionPersistencePreference(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(AUTH_REMEMBER_DEVICE_KEY);
  } catch {
    // Best-effort only.
  }
}

function clearAccessTokensFrom(storage: Storage | null): void {
  if (!storage) return;
  for (const key of AUTH_TOKEN_KEYS) {
    try {
      storage.removeItem(key);
    } catch {
      // Best-effort only.
    }
  }
}

function clearRefreshTokensFrom(storage: Storage | null): void {
  if (!storage) return;
  for (const key of REFRESH_TOKEN_KEYS) {
    try {
      storage.removeItem(key);
    } catch {
      // Best-effort only.
    }
  }
}

/** Remove access + refresh tokens from both storages (logout / failed auth cleanup). */
export function clearAllStoredAuthTokens(): void {
  if (typeof window === 'undefined') return;
  clearAccessTokensFrom(window.localStorage);
  clearRefreshTokensFrom(window.localStorage);
  clearAccessTokensFrom(window.sessionStorage);
  clearRefreshTokensFrom(window.sessionStorage);
}

/**
 * Clear per-user / per-tenant application state that must not survive a logout,
 * so the next user who signs in on the same browser never inherits the previous
 * user's data. This is a security boundary, not a convenience cleanup.
 */
const clearPerUserAppState = (): void => {
  if (typeof window === 'undefined') return;

  // Any key starting with one of these prefixes is per-user/per-tenant state
  // (Emma chat sessions and messages, cached workflows, analytics, reports).
  const clearPrefixes = [
    'emma_',
    'active_workflows',
    'crossEpicState',
    'cosellus_analytics_cache',
    'cosellus_analytics_metrics',
    'customReports',
    'customThemes',
  ];

  const sweep = (store: Storage) => {
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (key && clearPrefixes.some((p) => key.startsWith(p))) {
          toRemove.push(key);
        }
      }
      toRemove.forEach((k) => store.removeItem(k));
    } catch {
      /* storage access can throw in some privacy modes; ignore */
    }
  };

  sweep(window.localStorage);
  sweep(window.sessionStorage);
};

export const clearPersistedAuthState = (): void => {
  if (typeof window === 'undefined') return;
  clearAllStoredAuthTokens();
  clearTenantScope();
  clearPerUserAppState();
};

export const persistAccessToken = (token: string): void => {
  if (typeof window === 'undefined' || !token) return;

  const useLocal = getSessionPersistsToLocalStorage();
  if (useLocal) {
    clearAccessTokensFrom(window.sessionStorage);
    for (const key of AUTH_TOKEN_KEYS) {
      try {
        window.localStorage.setItem(key, token);
      } catch {
        // Best-effort only.
      }
    }
    try {
      window.sessionStorage.setItem('auth_token', token);
    } catch {
      // Best-effort only.
    }
  } else {
    clearAccessTokensFrom(window.localStorage);
    for (const key of AUTH_TOKEN_KEYS) {
      try {
        window.sessionStorage.setItem(key, token);
      } catch {
        // Best-effort only.
      }
    }
    try {
      window.sessionStorage.setItem('auth_token', token);
    } catch {
      // Best-effort only.
    }
  }
};

export const persistRefreshToken = (token: string): void => {
  if (typeof window === 'undefined' || !token) return;

  const useLocal = getSessionPersistsToLocalStorage();
  if (useLocal) {
    clearRefreshTokensFrom(window.sessionStorage);
    for (const key of REFRESH_TOKEN_KEYS) {
      try {
        window.localStorage.setItem(key, token);
      } catch {
        // Best-effort only.
      }
    }
  } else {
    clearRefreshTokensFrom(window.localStorage);
    for (const key of REFRESH_TOKEN_KEYS) {
      try {
        window.sessionStorage.setItem(key, token);
      } catch {
        // Best-effort only.
      }
    }
  }
};

export const getOrganizationIdFromStorage = (): string | null => {
  return getTenantScope().organizationId;
};

export const getActivePartitionIdFromStorage = (): string | null =>
  getTenantScope().activePartitionId;

export const sanitizeOrganizationHeader = (value: string | null | undefined): string | null =>
  sanitizeTenantScopeId(value);

export const sanitizeActivePartitionHeader = (value: string | null | undefined): string | null =>
  sanitizeTenantScopeId(value);

export const withApiAuthHeaders = (baseHeaders: HeadersInit = {}): Headers => {
  const headers = new Headers(baseHeaders);

  if (!headers.has('Authorization')) {
    const token = getBestAuthToken() || getAccessToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const explicitOrgId = sanitizeOrganizationHeader(headers.get('X-Organization-ID') || headers.get('X-Tenant-ID'));
  const storedTenantId = (typeof window !== 'undefined' ? window.localStorage.getItem('active_tenant_id') : null);
  const effectiveTenantId = explicitOrgId || getTenantScope().organizationId || storedTenantId;

  if (effectiveTenantId) {
    headers.set('X-Organization-ID', effectiveTenantId);
    headers.set('X-Tenant-ID', effectiveTenantId);
  }

  const explicitImpersonationReason = (headers.get('X-Impersonation-Reason') || '').trim();

  const explicitPartitionId = sanitizeActivePartitionHeader(headers.get('X-Active-Partition-ID'));
  if (explicitPartitionId) {
    headers.set('X-Active-Partition-ID', explicitPartitionId);
  } else {
    headers.delete('X-Active-Partition-ID');
    const partitionId = getTenantScope().activePartitionId;
    if (partitionId) {
      headers.set('X-Active-Partition-ID', partitionId);
    }
  }

  // Super-admin impersonation uses both X-Organization-ID + X-Impersonation-Reason.
  // The reason is propagated from centralized tenant scope state.
  if (explicitImpersonationReason) {
    headers.set('X-Impersonation-Reason', explicitImpersonationReason);
  } else {
    headers.delete('X-Impersonation-Reason');
    const reason = (getTenantScope().impersonationReason || '').trim();
    if (reason) {
      headers.set('X-Impersonation-Reason', reason);
    }
  }

  return headers;
};

export type { TenantScope };
export {
  clearTenantScope,
  getTenantScope,
  setActivePartitionScope,
  setImpersonationReason,
  setOrganizationScope,
};
